import {pathOr} from 'ramda';
import {FULFILLED} from 'redux-promise-middleware';
import {select, call, takeEvery, all} from 'redux-saga/effects';
import {
    pushDataLayer,
    createEECCheckoutStepEvent,
    EecCheckoutProductsType,
    PAYMENT_METHODS,
} from '@optimaxdev/analytics/mobile';

import {PRODUCT_TYPES} from 'constants/product';
import {
    getActiveShippingMethod,
    getIsPaymentMethodFulfilled,
    getCheckoutData,
} from 'selectors/checkout/checkout';
import {getPaymentMethods} from 'selectors/payments/payments';
import {getPaymentMethod} from 'sagas/globalDataLayer/globalDataLayerResolvers';
import {
    setOptionShippingMethod,
    setOptionPaymentMethod,
} from 'reducers/analytics/checkoutOption/checkoutOption';
import {waitForFulfilled} from 'libs/utils/utils';
import {getQuantityVariantV2} from 'libs/EEC/getVariant';
import {getCategoryV2} from 'libs/EEC/productToEECProduct';
import {convertPriceEEC} from 'libs/convertPrice/convertPrice';

import {CHECKOUT} from 'constants/actions';
import {
    getOrderReviewItems,
    isOrderReviewFulfilled,
} from 'selectors/checkout/orderReview/orderReview';

export const STEP = {
    DELIVERY: 1,
    PAYMENT: 2,
};

/**
 * Prepare and push checkoutOption ecommerce event
 *
 * @param {number} step - checkout step number
 * @param {string} option - checkout step option
 */
export const prepareDataForPush = (step: number, option: string) => {
    pushDataLayer({
        event: 'checkoutOption',
        ecommerce: {
            checkout_option: {
                actionField: {
                    step,
                    option,
                },
            },
        },
    });
};

/**
 * Get payment code
 * @returns {string} payment code
 */
export function* getPaymentCode() {
    yield call(waitForFulfilled, getIsPaymentMethodFulfilled, 'GET_PAYMENT_METHODS_FULFILLED');
    const {code} = yield call(getPaymentMethod);
    return code;
}

/**
 * Get list of keys payment methods
 * @returns {string[]} keys of payment methods
 */
export function* getPaymentMethodsKeysList() {
    yield call(waitForFulfilled, getIsPaymentMethodFulfilled, 'GET_PAYMENT_METHODS_FULFILLED');
    return Object.keys(yield select(getPaymentMethods));
}

/**
 * Send delivery step info to dataLayer
 */
export function* sendDeliveryStep() {
    const {title: option} = yield select(getActiveShippingMethod);
    yield call(prepareDataForPush, STEP.DELIVERY, option);
}

/**
 * Send payment step info to dataLayer
 */
export function* sendPaymentStep() {
    const option = yield call(getPaymentCode);
    yield call(prepareDataForPush, STEP.PAYMENT, option);
}

/**
 * Returns additional fields for EEC Event whichever step
 * @param {number} step Checkout step
 * @returns {Object} Object with additional field
 */
export function* getAdditionalFields(step: number) {
    if (step === 1) {
        const list: string[] = yield call(getPaymentMethodsKeysList);
        const availablePaymentMethods: string = list
            .reduce(
                (acc: string[], value: string) =>
                    PAYMENT_METHODS[value] ? [...acc, PAYMENT_METHODS[value]] : acc,
                [],
            )
            .join(',');
        return {availablePaymentMethods};
    }

    return {};
}

/**
 * Send checkout step event to dataLayer
 */
export function* sendCheckoutStep() {
    yield call(
        waitForFulfilled,
        isOrderReviewFulfilled,
        `${CHECKOUT.GET_ORDER_REVIEW_ITEMS}_${FULFILLED}`,
    );

    const [{currentStep: step = 0}, quoteItems] = yield all([
        select(getCheckoutData),
        select(getOrderReviewItems),
    ]);
    const eecCheckoutStepEventBase: {
        step: number;
        products: EecCheckoutProductsType;
    } = {
        step: step + 1,
        products: quoteItems.map(({sku, product, discountedPrice, totalFinalPrice, qty}) => ({
            id: sku,
            name: product.name,
            price:
                product.type === PRODUCT_TYPES.CONTACT_LENSES
                    ? convertPriceEEC(discountedPrice)
                    : convertPriceEEC(totalFinalPrice),
            brand: pathOr('', ['brand', 'label'], product),
            category: getCategoryV2(product),
            variant: getQuantityVariantV2(product),
            quantity: qty,
        })),
    };

    const eecCheckoutStepEventDetailed = {
        ...eecCheckoutStepEventBase,
        ...(yield getAdditionalFields(step)),
    };

    yield call(pushDataLayer, createEECCheckoutStepEvent(eecCheckoutStepEventDetailed));
}

/**
 * Checkout option saga
 */
export default function* checkoutOptionSaga() {
    yield takeEvery('SET_CURRENT_STEP', sendCheckoutStep);
    yield takeEvery(setOptionShippingMethod, sendDeliveryStep);
    yield takeEvery(setOptionPaymentMethod, sendPaymentStep);
}
